import { useState, useRef, useEffect } from 'react';
import {
  Upload,
  FileText,
  Loader2,
  Download,
  AlertTriangle,
  CheckCircle2,
  Sparkles,
  ArrowRight,
  Copy,
  Check,
  Info,
  RefreshCw,
  Building2,
  Zap,
} from 'lucide-react';
import { api } from '@/lib/api';
import { useTaxStore } from '@/store/taxStore';
import { cn } from '@/lib/utils';

interface Business {
  id: string;
  name: string;
}

interface AnalysisResult {
  detectedType: 'crypto' | 'stocks' | 'expenses' | 'income' | 'deductions' | 'w2' | 'unknown';
  confidence: number;
  description: string;
  extractedData: any[];
  formattedCsv: string;
  csvHeaders: string[];
  warnings: string[];
  importInstructions: string;
}

const typeLabels: Record<string, { label: string; color: string; icon: string }> = {
  crypto: { label: 'Cryptocurrency', color: 'text-orange-500', icon: 'TrendingUp' },
  stocks: { label: 'Stocks/Equities', color: 'text-blue-500', icon: 'TrendingUp' },
  expenses: { label: 'Business Expenses', color: 'text-red-500', icon: 'Receipt' },
  income: { label: 'Income', color: 'text-green-500', icon: 'DollarSign' },
  deductions: { label: 'Tax Deductions', color: 'text-purple-500', icon: 'Percent' },
  w2: { label: 'W-2 Wages', color: 'text-cyan-500', icon: 'FileText' },
  unknown: { label: 'Unknown', color: 'text-muted-foreground', icon: 'HelpCircle' },
};

export function SmartImportPage() {
  const { selectedYear } = useTaxStore();
  const [file, setFile] = useState<File | null>(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState<{
    fileName: string;
    fileType: string;
    analysis: AnalysisResult;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Business selection for expenses/income
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [selectedBusiness, setSelectedBusiness] = useState<string>('');
  const [loadingBusinesses, setLoadingBusinesses] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importSuccess, setImportSuccess] = useState<string | null>(null);

  // Exchange/broker selection for crypto/stocks
  const [selectedExchange, setSelectedExchange] = useState<string>('');
  const [selectedBroker, setSelectedBroker] = useState<string>('');

  const cryptoExchanges = [
    { id: 'gemini', name: 'Gemini' },
    { id: 'coinbase', name: 'Coinbase' },
    { id: 'kraken', name: 'Kraken' },
    { id: 'binance', name: 'Binance' },
    { id: 'trust_wallet', name: 'Trust Wallet' },
    { id: 'other', name: 'Other' },
  ];

  const stockBrokers = [
    { id: 'fidelity', name: 'Fidelity' },
    { id: 'schwab', name: 'Charles Schwab' },
    { id: 'robinhood', name: 'Robinhood' },
    { id: 'td_ameritrade', name: 'TD Ameritrade' },
    { id: 'etrade', name: 'E*TRADE' },
    { id: 'other', name: 'Other' },
  ];

  // Fetch businesses on mount
  useEffect(() => {
    const fetchBusinesses = async () => {
      setLoadingBusinesses(true);
      try {
        const response = await api.listBusinesses();
        setBusinesses(response.businesses || []);
      } catch (err) {
        console.error('Failed to fetch businesses:', err);
      } finally {
        setLoadingBusinesses(false);
      }
    };
    fetchBusinesses();
  }, []);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      setResult(null);
      setError(null);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files?.[0];
    if (droppedFile) {
      setFile(droppedFile);
      setResult(null);
      setError(null);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const getFileType = (fileName: string): 'csv' | 'pdf' | 'txt' | 'json' | 'xlsx' => {
    const ext = fileName.split('.').pop()?.toLowerCase();
    if (ext === 'csv') return 'csv';
    if (ext === 'pdf') return 'pdf';
    if (ext === 'json') return 'json';
    if (ext === 'xlsx' || ext === 'xls') return 'xlsx';
    return 'txt';
  };

  const analyzeFile = async () => {
    if (!file) return;

    setAnalyzing(true);
    setError(null);
    setResult(null);

    try {
      const fileType = getFileType(file.name);

      // Read file content
      const isBinaryFile = fileType === 'pdf' || fileType === 'xlsx';
      const content = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          if (isBinaryFile) {
            // For binary files (PDF, XLSX), use base64
            const base64 = (reader.result as string).split(',')[1];
            resolve(base64);
          } else {
            resolve(reader.result as string);
          }
        };
        reader.onerror = reject;
        if (isBinaryFile) {
          reader.readAsDataURL(file);
        } else {
          reader.readAsText(file);
        }
      });

      const response = await api.smartImport({
        fileContent: content,
        fileName: file.name,
        fileType,
        year: selectedYear,
      });

      setResult(response);
    } catch (err: any) {
      setError(err.message || 'Failed to analyze file');
    } finally {
      setAnalyzing(false);
    }
  };

  const downloadCsv = () => {
    if (!result?.analysis.formattedCsv) return;

    const blob = new Blob([result.analysis.formattedCsv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const baseName = result.fileName.replace(/\.[^.]+$/, '');
    a.download = `${baseName}-formatted-${result.analysis.detectedType}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const copyToClipboard = async () => {
    if (!result?.analysis.formattedCsv) return;
    await navigator.clipboard.writeText(result.analysis.formattedCsv);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const resetImport = () => {
    setFile(null);
    setResult(null);
    setError(null);
    setSelectedBusiness('');
    setSelectedExchange('');
    setSelectedBroker('');
    setImportSuccess(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Direct import for expenses/income/crypto/stocks
  const handleDirectImport = async () => {
    if (!result || !result.analysis.formattedCsv) return;

    const detectedType = result.analysis.detectedType;

    // Validate selection based on type
    if ((detectedType === 'expenses' || detectedType === 'income') && !selectedBusiness) return;
    if (detectedType === 'crypto' && !selectedExchange) return;
    if (detectedType === 'stocks' && !selectedBroker) return;

    setImporting(true);
    setError(null);
    setImportSuccess(null);

    try {
      if (detectedType === 'expenses') {
        const response = await api.importExpenses({
          businessId: selectedBusiness,
          year: selectedYear,
          csvData: result.analysis.formattedCsv,
        });
        setImportSuccess(`Successfully imported ${response.imported} expenses totaling $${response.totalAmount.toLocaleString()}`);
      } else if (detectedType === 'income') {
        const response = await api.importIncome({
          businessId: selectedBusiness,
          year: selectedYear,
          csvData: result.analysis.formattedCsv,
        });
        setImportSuccess(`Successfully imported ${response.imported} income records totaling $${response.totalAmount.toLocaleString()}`);
      } else if (detectedType === 'crypto') {
        const response = await api.importCrypto({
          exchange: selectedExchange,
          year: selectedYear,
          csvData: result.analysis.formattedCsv,
        });
        setImportSuccess(`Successfully imported crypto transactions from ${cryptoExchanges.find(e => e.id === selectedExchange)?.name || selectedExchange}`);
      } else if (detectedType === 'stocks') {
        const response = await api.importStocks({
          broker: selectedBroker,
          year: selectedYear,
          csvData: result.analysis.formattedCsv,
        });
        setImportSuccess(`Successfully imported stock transactions from ${stockBrokers.find(b => b.id === selectedBroker)?.name || selectedBroker}`);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to import data');
    } finally {
      setImporting(false);
    }
  };

  const typeInfo = result?.analysis.detectedType
    ? typeLabels[result.analysis.detectedType]
    : null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center gap-2">
          <Sparkles className="h-6 w-6 text-primary" />
          <h2 className="text-xl font-semibold">Smart Import</h2>
        </div>
        <p className="text-muted-foreground mt-1">
          Upload any file and AI will analyze and format it for importing
        </p>
      </div>

      {/* AI Notice */}
      <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 flex items-start gap-3">
        <Info className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
        <div className="text-sm">
          <p className="font-medium text-primary">This feature uses external AI</p>
          <p className="text-muted-foreground mt-1">
            Your file content will be sent to Anthropic's Claude AI for analysis.
            Ensure your Anthropic API key is configured in Settings.
            Do not upload files containing sensitive personal information beyond tax-related data.
          </p>
        </div>
      </div>

      {/* Upload Area */}
      {!result && (
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onClick={() => fileInputRef.current?.click()}
          className={cn(
            'border-2 border-dashed rounded-xl p-12 text-center cursor-pointer transition-all',
            file
              ? 'border-primary bg-primary/5'
              : 'border-border hover:border-primary/50 hover:bg-muted/30'
          )}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.txt,.json,.pdf,.xlsx,.xls"
            onChange={handleFileSelect}
            className="hidden"
          />

          {file ? (
            <div className="space-y-4">
              <div className="h-16 w-16 mx-auto rounded-full bg-primary/10 flex items-center justify-center">
                <FileText className="h-8 w-8 text-primary" />
              </div>
              <div>
                <p className="font-medium text-lg">{file.name}</p>
                <p className="text-sm text-muted-foreground">
                  {(file.size / 1024).toFixed(1)} KB
                </p>
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  analyzeFile();
                }}
                disabled={analyzing}
                className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-colors disabled:opacity-50"
              >
                {analyzing ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" />
                    Analyzing with AI...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-5 w-5" />
                    Analyze File
                  </>
                )}
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="h-16 w-16 mx-auto rounded-full bg-muted flex items-center justify-center">
                <Upload className="h-8 w-8 text-muted-foreground" />
              </div>
              <div>
                <p className="font-medium text-lg">Drop a file here or click to browse</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Supports PDF, Excel (XLSX), CSV, TXT, and JSON files
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-red-500 flex-shrink-0" />
          <div>
            <p className="font-medium text-red-500">Analysis Failed</p>
            <p className="text-sm text-muted-foreground mt-1">{error}</p>
          </div>
        </div>
      )}

      {/* Results */}
      {result && (
        <div className="space-y-6">
          {/* Detection Result */}
          <div className="bg-card border border-border rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Analysis Result</h3>
              <button
                onClick={resetImport}
                className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                <RefreshCw className="h-4 w-4" />
                Analyze Another File
              </button>
            </div>

            <div className="flex items-center gap-4 mb-4">
              <div className={cn(
                'h-12 w-12 rounded-full flex items-center justify-center',
                result.analysis.detectedType !== 'unknown'
                  ? 'bg-green-500/10'
                  : 'bg-yellow-500/10'
              )}>
                {result.analysis.detectedType !== 'unknown' ? (
                  <CheckCircle2 className="h-6 w-6 text-green-500" />
                ) : (
                  <AlertTriangle className="h-6 w-6 text-yellow-500" />
                )}
              </div>
              <div>
                <p className="font-medium">
                  Detected: <span className={typeInfo?.color}>{typeInfo?.label}</span>
                </p>
                <p className="text-sm text-muted-foreground">
                  Confidence: {Math.round(result.analysis.confidence * 100)}%
                </p>
              </div>
            </div>

            <p className="text-muted-foreground">{result.analysis.description}</p>

            {result.analysis.warnings.length > 0 && (
              <div className="mt-4 p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                <p className="text-sm font-medium text-yellow-600 dark:text-yellow-400 mb-1">Warnings:</p>
                <ul className="text-sm text-muted-foreground space-y-1">
                  {result.analysis.warnings.map((warning, idx) => (
                    <li key={idx}>• {warning}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Business Selection for Expenses/Income */}
          {(result.analysis.detectedType === 'expenses' || result.analysis.detectedType === 'income') && (
            <div className="bg-card border border-border rounded-xl p-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Building2 className="h-5 w-5 text-primary" />
                Select Business for Import
              </h3>

              {importSuccess ? (
                <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4 flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-green-500">Import Successful!</p>
                    <p className="text-sm text-muted-foreground mt-1">{importSuccess}</p>
                    <p className="text-sm text-muted-foreground mt-2">
                      View your imported data on the <strong>Businesses</strong> page.
                    </p>
                  </div>
                </div>
              ) : (
                <>
                  {loadingBusinesses ? (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Loading businesses...
                    </div>
                  ) : businesses.length === 0 ? (
                    <div className="p-4 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                      <p className="text-sm text-yellow-600 dark:text-yellow-400">
                        No businesses found. Please create a business on the <strong>Businesses</strong> page first,
                        then return here to import your {result.analysis.detectedType}.
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium mb-2">
                          Which business should this {result.analysis.detectedType === 'expenses' ? 'expense' : 'income'} data be associated with?
                        </label>
                        <select
                          value={selectedBusiness}
                          onChange={(e) => setSelectedBusiness(e.target.value)}
                          className="w-full bg-background border border-border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                        >
                          <option value="">Select a business...</option>
                          {businesses.map((business) => (
                            <option key={business.id} value={business.id}>
                              {business.name}
                            </option>
                          ))}
                        </select>
                      </div>

                      <button
                        onClick={handleDirectImport}
                        disabled={!selectedBusiness || importing}
                        className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {importing ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Importing...
                          </>
                        ) : (
                          <>
                            <Zap className="h-4 w-4" />
                            Import Directly to {selectedBusiness ? businesses.find(b => b.id === selectedBusiness)?.name : 'Business'}
                          </>
                        )}
                      </button>

                      <p className="text-xs text-muted-foreground">
                        Or download the CSV below to import manually later.
                      </p>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Exchange Selection for Crypto */}
          {result.analysis.detectedType === 'crypto' && (
            <div className="bg-card border border-border rounded-xl p-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-orange-500" />
                Import Cryptocurrency Transactions
              </h3>

              {importSuccess ? (
                <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4 flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-green-500">Import Successful!</p>
                    <p className="text-sm text-muted-foreground mt-1">{importSuccess}</p>
                    <p className="text-sm text-muted-foreground mt-2">
                      View your imported data on the <strong>Investments</strong> page.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Which exchange is this data from?
                    </label>
                    <select
                      value={selectedExchange}
                      onChange={(e) => setSelectedExchange(e.target.value)}
                      className="w-full bg-background border border-border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                    >
                      <option value="">Select an exchange...</option>
                      {cryptoExchanges.map((exchange) => (
                        <option key={exchange.id} value={exchange.id}>
                          {exchange.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <button
                    onClick={handleDirectImport}
                    disabled={!selectedExchange || importing}
                    className="flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg font-medium hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {importing ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Importing...
                      </>
                    ) : (
                      <>
                        <Zap className="h-4 w-4" />
                        Import to Investments
                      </>
                    )}
                  </button>

                  <p className="text-xs text-muted-foreground">
                    Or download the CSV below to import manually later.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Broker Selection for Stocks */}
          {result.analysis.detectedType === 'stocks' && (
            <div className="bg-card border border-border rounded-xl p-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-blue-500" />
                Import Stock Transactions
              </h3>

              {importSuccess ? (
                <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4 flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-green-500">Import Successful!</p>
                    <p className="text-sm text-muted-foreground mt-1">{importSuccess}</p>
                    <p className="text-sm text-muted-foreground mt-2">
                      View your imported data on the <strong>Investments</strong> page.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Which broker is this data from?
                    </label>
                    <select
                      value={selectedBroker}
                      onChange={(e) => setSelectedBroker(e.target.value)}
                      className="w-full bg-background border border-border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                    >
                      <option value="">Select a broker...</option>
                      {stockBrokers.map((broker) => (
                        <option key={broker.id} value={broker.id}>
                          {broker.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <button
                    onClick={handleDirectImport}
                    disabled={!selectedBroker || importing}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {importing ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Importing...
                      </>
                    ) : (
                      <>
                        <Zap className="h-4 w-4" />
                        Import to Investments
                      </>
                    )}
                  </button>

                  <p className="text-xs text-muted-foreground">
                    Or download the CSV below to import manually later.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Data Preview */}
          {result.analysis.extractedData.length > 0 && (
            <div className="bg-card border border-border rounded-xl p-6">
              <h3 className="text-lg font-semibold mb-4">
                Extracted Data ({result.analysis.extractedData.length} records)
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      {result.analysis.csvHeaders.map((header, idx) => (
                        <th key={idx} className="text-left py-2 px-3 font-medium">
                          {header}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {result.analysis.extractedData.slice(0, 10).map((row, rowIdx) => (
                      <tr key={rowIdx} className="border-b border-border/50">
                        {result.analysis.csvHeaders.map((header, colIdx) => (
                          <td key={colIdx} className="py-2 px-3 text-muted-foreground">
                            {String(row[header] ?? row[header.toLowerCase()] ?? '-')}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
                {result.analysis.extractedData.length > 10 && (
                  <p className="text-sm text-muted-foreground mt-2 text-center">
                    Showing 10 of {result.analysis.extractedData.length} records
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Import Instructions */}
          {result.analysis.importInstructions && (
            <div className="bg-card border border-border rounded-xl p-6">
              <h3 className="text-lg font-semibold mb-4">Import Instructions</h3>
              <p className="text-muted-foreground">{result.analysis.importInstructions}</p>
            </div>
          )}

          {/* Download Section */}
          {result.analysis.formattedCsv && (
            <div className="bg-card border border-border rounded-xl p-6">
              <h3 className="text-lg font-semibold mb-4">Formatted CSV</h3>
              <div className="bg-muted rounded-lg p-4 font-mono text-sm overflow-x-auto max-h-48 overflow-y-auto">
                <pre className="whitespace-pre-wrap">{result.analysis.formattedCsv}</pre>
              </div>
              <div className="flex gap-3 mt-4">
                <button
                  onClick={downloadCsv}
                  className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-colors"
                >
                  <Download className="h-4 w-4" />
                  Download CSV
                </button>
                <button
                  onClick={copyToClipboard}
                  className="flex items-center gap-2 px-4 py-2 bg-muted text-foreground rounded-lg font-medium hover:bg-muted/80 transition-colors"
                >
                  {copied ? (
                    <>
                      <Check className="h-4 w-4" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="h-4 w-4" />
                      Copy to Clipboard
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Next Steps */}
          <div className="bg-primary/5 border border-primary/20 rounded-xl p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <ArrowRight className="h-5 w-5 text-primary" />
              Next Steps
            </h3>
            <div className="space-y-3 text-sm">
              {result.analysis.detectedType === 'crypto' && (
                <p>
                  1. Download the formatted CSV above<br />
                  2. Go to <strong>Investments</strong> page<br />
                  3. Use the <strong>Import Crypto</strong> option and select the downloaded CSV
                </p>
              )}
              {result.analysis.detectedType === 'stocks' && (
                <p>
                  1. Download the formatted CSV above<br />
                  2. Go to <strong>Investments</strong> page<br />
                  3. Use the <strong>Import Stocks</strong> option and select the downloaded CSV
                </p>
              )}
              {result.analysis.detectedType === 'expenses' && (
                <p>
                  1. Download the formatted CSV above<br />
                  2. Go to <strong>Businesses</strong> page<br />
                  3. Select a business and use <strong>Import Expenses</strong> with the downloaded CSV
                </p>
              )}
              {result.analysis.detectedType === 'income' && (
                <p>
                  1. Download the formatted CSV above<br />
                  2. Go to <strong>Businesses</strong> page<br />
                  3. Select a business and use <strong>Import Income</strong> with the downloaded CSV
                </p>
              )}
              {result.analysis.detectedType === 'deductions' && (
                <p>
                  1. Download the formatted CSV above<br />
                  2. Go to <strong>Deductions</strong> page<br />
                  3. You can manually add deductions based on the extracted data
                </p>
              )}
              {result.analysis.detectedType === 'w2' && (
                <p>
                  1. Review the extracted W-2 data above<br />
                  2. Go to <strong>Documents</strong> page<br />
                  3. Upload the original W-2 PDF for automatic extraction
                </p>
              )}
              {result.analysis.detectedType === 'unknown' && (
                <p>
                  The file type could not be determined. Please review the extracted data
                  and manually enter it in the appropriate section of the app.
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Supported Formats Info */}
      {!result && !file && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-card border border-border rounded-lg p-4">
            <h4 className="font-medium mb-2">Cryptocurrency</h4>
            <p className="text-sm text-muted-foreground">
              Exports from Gemini, Kraken, Coinbase, Trust Wallet, and other exchanges
            </p>
          </div>
          <div className="bg-card border border-border rounded-lg p-4">
            <h4 className="font-medium mb-2">Stock Trades</h4>
            <p className="text-sm text-muted-foreground">
              Exports from Fidelity, Schwab, TD Ameritrade, Robinhood, and more
            </p>
          </div>
          <div className="bg-card border border-border rounded-lg p-4">
            <h4 className="font-medium mb-2">Expenses & Income</h4>
            <p className="text-sm text-muted-foreground">
              Bank exports, QuickBooks, spreadsheets, and any CSV with financial data
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
